import type { GameStatePayload } from './protocol/messages.ts';

/**
 * Tab completion, the pure half (clients/tab-completion.md §2.2).
 *
 * Two token classes, one rule: **commands come from the catalog, ids come from the projection.** Nothing
 * is ever completed from an authored string the session does not actually contain - a candidate list that
 * offers a seat this scenario does not have is worse than no completion, because it teaches a name that
 * will then be refused.
 *
 * DOM-free and side-effect-free on purpose: the terminal knows nothing about commands, the views know
 * nothing about keystrokes, and this is table-tested the way the store is.
 */

/** Everything a completer may read: the projected board, and the client-side lists that are not on it. */
export interface CompletionFold {
  /** The projected engine state, or null before the first delta (a lobby, or a chat-only session). */
  game: GameStatePayload | null;
  /** Panel names, which live in the view layer rather than the projection. */
  panels: readonly string[];
  /**
   * The public session list, for the lobby's `join`. Not part of the projection - the lobby has no
   * session to project - but it is the same rule: the ids offered are the ids the server just listed.
   */
  sessions: readonly string[];
  /**
   * The packaged deck ids, for the lobby's `create ... game <deck>` (DEC-17 M4 increment 8b). Same rule
   * again: what completes is what the SERVER listed, never a set typed into the client - which is what
   * keeps a deck shipped tomorrow completable without a client release.
   */
  decks: readonly string[];
  /**
   * The short ids of lines that did not send, for `/retry` and `/discard` (the outbox). Same rule
   * again: what completes is what is actually waiting, so a player never types an id at a line that
   * has since gone out.
   */
  pending: readonly string[];
}

/** Produces the candidates for one argument position. Pure: same fold, same argv, same answer. */
export type Completer = (fold: CompletionFold, argv: readonly string[], i: number) => string[];

/**
 * One command: what it looks like, and what completes at each argument.
 *
 * `args` is positional and REQUIRED to be declared, even as an empty array - the point of folding
 * completion into the same table as the usage string is that adding a command without deciding its
 * completers becomes a type error rather than a silent gap (§2.1).
 */
export interface CommandSpec {
  usage: string;
  args: readonly Completer[];
  /**
   * The completer for any position past `args` - for the commands whose arguments are NOT positional.
   *
   * `create <name> [game [deck]] [pub]` is the case that needed it: the name is free text of any word
   * count, so the flag and the deck do not sit at fixed indices and a positional table silently stops
   * completing after two words. A `rest` completer reads `argv` and decides for itself, which is the
   * honest shape for that grammar; every other command stays positional.
   */
  rest?: Completer;
}

/** What to draw: the candidates, and where in the text they would be inserted. */
export interface Completion {
  items: string[];
  /** Index in `text` where the token being completed starts. */
  from: number;
}

const EMPTY: Completion = { items: [], from: 0 };

/** The longest prefix every candidate shares - what a Tab with several matches extends to. */
export function commonPrefix(items: readonly string[]): string {
  if (items.length === 0) return '';
  let prefix = items[0]!;
  for (const item of items.slice(1)) {
    let i = 0;
    while (i < prefix.length && i < item.length && prefix[i] === item[i]) i++;
    prefix = prefix.slice(0, i);
    if (!prefix) break;
  }
  return prefix;
}

/**
 * The candidates for `text` at `caret`.
 *
 * Modes, decided by the first character of the line and the token under the caret: a command NAME, a
 * command ARGUMENT (delegated to that command's completer), or a MENTION - the public half of the talk
 * pair, which gets the same treatment as the slash half (§2.3).
 */
export function complete(input: {
  commands: ReadonlyMap<string, CommandSpec>;
  fold: CompletionFold;
  text: string;
  caret: number;
}): Completion {
  const { commands, fold } = input;
  const head = input.text.slice(0, input.caret);

  // The token under the caret, and where it starts. Everything downstream is prefix-filtering.
  const start = head.lastIndexOf(' ') + 1;
  const token = head.slice(start);

  // A mention: `@eng` → seat keys. Only in the first token, because `@` mid-sentence is prose.
  if (head.startsWith('@') && start === 0) {
    const prefix = token.slice(1);
    return offer(
      seatKeys(fold).filter((k) => k.startsWith(prefix)),
      1,
    );
  }

  if (!head.startsWith('/')) {
    return EMPTY;
  }

  const argv = head.split(/\s+/);
  const name = (argv[0] ?? '').slice(1);

  // The command name itself.
  if (argv.length === 1) {
    return offer([...commands.keys()].filter((k) => k.startsWith(name)).sort(), 1);
  }

  // An argument: the command's own completer for this position, prefix-filtered.
  const spec = commands.get(name);
  const index = argv.length - 2;
  const completer = spec?.args[index] ?? spec?.rest;
  if (!completer) {
    return EMPTY;
  }

  const args = argv.slice(1);
  return offer(
    completer(fold, args, index).filter((c) => c.startsWith(token)),
    start,
  );
}

function offer(items: string[], from: number): Completion {
  return { items: [...new Set(items)], from };
}

function seatKeys(fold: CompletionFold): string[] {
  return (fold.game?.seats ?? []).map((s) => s.key);
}

/**
 * The completers, all reading the projection and nothing else (§1). Exported as one object so the command
 * table reads as a spec: `args: [complete.decisionId, complete.decisionOption]`.
 */
export const complete_ = {
  /** Seats nobody holds - the only ones `/claim` can succeed on. */
  freeSeat: (fold: CompletionFold): string[] =>
    (fold.game?.seats ?? []).filter((s) => s.claimable).map((s) => s.key),

  /** Every seat, yours first: the commands that take a seat are usually about your own. */
  seat: (fold: CompletionFold): string[] => {
    const seats = fold.game?.seats ?? [];
    return [...seats.filter((s) => s.mine), ...seats.filter((s) => !s.mine)].map((s) => s.key);
  },

  /** Your seat's verbs - `/do` on a seat you do not hold is a refusal waiting to happen. */
  verb: (fold: CompletionFold): string[] =>
    (fold.game?.seats ?? []).find((s) => s.mine)?.powers.slice() ?? [],

  /** Open decision ids. */
  decisionId: (fold: CompletionFold): string[] => (fold.game?.decisions ?? []).map((d) => d.id),

  /** The options of the decision named in the previous argument - not every option in the queue. */
  decisionOption: (fold: CompletionFold, argv: readonly string[]): string[] =>
    (fold.game?.decisions ?? []).find((d) => d.id === argv[0])?.options.slice() ?? [],

  /** The pending event's option ids. */
  eventOption: (fold: CompletionFold): string[] =>
    (fold.game?.activeEvent?.options ?? []).map((o) => o.id),

  /**
   * The stakeholders this session actually has (npc-llm-layer §7.5b).
   *
   * Outside-world keys, NOT seats: a promise is owed to the company on the other end of the call, and
   * `/promise comms ...` would be a promise to a colleague, which the engine refuses.
   */
  stakeholder: (fold: CompletionFold): string[] =>
    (fold.game?.stakeholders ?? []).map((s) => s.key),

  /** Open promises - what `/kept` and `/broke` can name. */
  promiseId: (fold: CompletionFold): string[] => (fold.game?.promises ?? []).map((p) => p.id),

  /** Backlog item ids. */
  itemId: (fold: CompletionFold): string[] => (fold.game?.backlog ?? []).map((b) => b.id),

  /** Policy ids in the ledger. */
  policyId: (fold: CompletionFold): string[] => (fold.game?.policies ?? []).map((p) => p.id),

  /** The amendments THIS policy offers. */
  policyVariant: (fold: CompletionFold, argv: readonly string[]): string[] =>
    (fold.game?.policies ?? []).find((p) => p.id === argv[0])?.amendments.slice() ?? [],

  /** Key-result ids on the board. */
  krId: (fold: CompletionFold): string[] => (fold.game?.okrs.keyResults ?? []).map((k) => k.id),

  /**
   * Metric names the board actually carries.
   *
   * Quietly the most valuable completer here: hand-typed KR expressions are not type-checked yet
   * (meeting-llm-layer.md §3.3), so offering the real metric names makes the mistyped-metric mistake
   * hard to MAKE while the validator gap waits for its plumbing.
   */
  metric: (fold: CompletionFold): string[] => Object.keys(fold.game?.meters ?? {}).sort(),

  /** Panel names. */
  panel: (fold: CompletionFold): string[] => [...fold.panels],

  /** The scenes a player can open. */
  scene: (): string[] => ['standup', 'planning', 'governance'],

  /** The mood enum - closed, and the engine rejects anything else. */
  mood: (): string[] => ['Happy', 'Satisfied', 'Stressed', 'Angry', 'Bored'],

  /**
   * `/ask` topics: the closed key set, with the LIVE ids filled in - `decision:comms-posture` rather
   * than `decision:<id>`, so the completion is typeable rather than a shape to copy.
   */
  askTopic: (fold: CompletionFold): string[] => [
    'mood',
    ...(fold.game?.decisions ?? []).map((d) => `decision:${d.id}`),
  ],

  /** Session ids from the last list - what `join` takes. */
  sessionId: (fold: CompletionFold): string[] => [...fold.sessions],

  /** Nothing completes here - declared so a command can say so rather than omit it. */
  none: (): string[] => [],
};
